import React, { useEffect, useState } from "react";
import L from "leaflet";
import "./AddTrack.css";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ThinFooter from "../Footer/ThinFooter";
import ContentWrapper from "../Atomics/ContentWrapper/ContentWrapper";
import axiosInstance from "../../axiosInstance";
import { useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useHistory } from "react-router-dom";
import Spinner from 'react-bootstrap/Spinner';

const UpdateTrack = () => {
  const history = useHistory();
  const { id } = useParams();
  const [trackData, setTrackData] = useState("");
  const [map, setMap] = useState(null);
  const [drawnShapes, setDrawnShapes] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const [trackDetails, setTrackDetails] = useState({
    name: "",
    address: "",
    image: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("Authorization token not found. Please log in.");
      return;
    }

    layouts.forEach((layout, index) => {
      const mapId = `map-${index}`;

      if (document.getElementById(mapId) && !layout.mapInstance) {
        const mapInstance = L.map(mapId, {
          center: [39.5384748, -122.3311959],
          zoom: 14,
          minZoom: 5,
          maxZoom: 15,
          zoomControl: true,
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(mapInstance);

        setLayouts((prevLayouts) => {
          const updatedLayouts = [...prevLayouts];
          updatedLayouts[index].mapInstance = mapInstance;
          return updatedLayouts;
        });

        const drawControl = new L.Control.Draw({
          draw: {
            polygon: true,
            rectangle: true,
            marker: false,
            circle: false,
            polyline: false,
            circlemarker: false,
          },
        });

        mapInstance.addControl(drawControl);

        mapInstance.on(L.Draw.Event.CREATED, function (event) {
          const layer = event.layer;
          const coordinates = layer.getLatLngs();
          setDrawnShapes((prevShapes) => [...prevShapes, coordinates]);
          layer.addTo(mapInstance);
        });
      }
    });
  }, [layouts]);
  useEffect(() => {
    // Automatically submit coordinates when jsonInput changes
    layouts.forEach((layout, index) => {
      if (layout.jsonInput) {
        handleJsonSubmit(index);
      }
    });
  }, [layouts]);

  useEffect(() => {
    if (id) {
      getSingleTrackData();
    }

    const token = localStorage.getItem("token");
    if (!token) {
      console.error("Authorization token not found. Please log in.");
      return;
    }
  }, [id]);

  const getSingleTrackData = async () => {
    setLoading(true); // Set loading to true when upload starts
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(`/tracks/getTrackById/${id}`, {
        headers: {
          Authorization: token,
        },
      });

      const track = res?.data?.data?.track;

      if (track) {
        // Update track details (name, location, description)
        setTrackDetails({
          name: track.name,
          image: track.description,
          address: track.location,
        });
        setLayouts(track.coordinates.map(layout => ({
          layoutName: layout.name,
          jsonInput: JSON.stringify(layout.coordinates) || ""
        })) || [])
        setLoading(false);
      }

    } catch (error) {
      console.error("Error fetching track data:", error);
      setLoading(false);
    }
  };


  // Handle JSON input submission
  const handleJsonSubmit = (index) => {
    try {
      const layout = layouts[index];
      const coordinates = JSON.parse(layout.jsonInput);

      // Get the specific map instance for this layout
      const mapInstance = layout.mapInstance;

      if (mapInstance) {
        // Clear existing polygon layers on the specific map
        mapInstance.eachLayer((layer) => {
          if (layer instanceof L.Polygon) {
            mapInstance.removeLayer(layer);
          }
        });

        // Draw the new polygon on the specific map
        const polygon = L.polygon(coordinates.map(([lng, lat]) => [lat, lng]), {
          color: "red",
          weight: 4,
        }).addTo(mapInstance);

        mapInstance.fitBounds(polygon.getBounds());
        mapInstance.zoomControl.setPosition('topright');
      }
    } catch (error) {
      console.error("Invalid JSON input", error);
      toast("Invalid JSON input. Please provide a valid JSON array of coordinates.");
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
  
    const maxSizeInKB = 500; // 500KB
    const fileSizeInKB = file.size / 1024;
  
    // Check if the file size exceeds the maximum limit
    if (fileSizeInKB > maxSizeInKB) {
      toast.error(`File size exceeds the maximum limit of ${maxSizeInKB}KB.`);
      return; // Stop further execution if the file is too large
    }
  
    setLoading(true); // Set loading to true when upload starts
  
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const formData = new FormData();
        formData.append("image", file); // Make sure to pass the correct file object
  
        // Upload image to S3
        const response = await fetch('https://beta.autotrak.ai/api/aws/upload', {
          method: 'POST',
          body: formData,
        });
  
        // Parse the JSON response
        const data = await response.json();
        const s3Url = data.data.url; // Access the URL correctly
  
        setTrackDetails((prevDetails) => ({ ...prevDetails, image: s3Url }));
      } catch (error) {
        toast.error('Failed to upload this image');
        console.log(error);
      } finally {
        setLoading(false); // Reset loading to false after upload completes
      }
    };
  
    reader.readAsDataURL(file);
  };
  

  const handleRemoveImage = () => {
    setImageLoaded(false)
    setTrackDetails((prevDetails) => ({ ...prevDetails, image: "" }));
  };

  const handleSaveChanges = async () => {
    if (!trackDetails.name || !trackDetails.image || !trackDetails.address) {
      toast.error("Please fill out all required fields.");
      return;
    }

    const coordinates = layouts.map((layout) => {
      try {
        const parsedCoordinates = layout.jsonInput ? JSON.parse(layout.jsonInput) : [];

        return {
          name: layout.layoutName || "Unnamed Layout",
          coordinates: Array.isArray(parsedCoordinates) ? parsedCoordinates : [],
        };
      } catch (error) {
        console.error("Invalid JSON input for layout", layout.layoutName, error);
        return {
          name: layout.layoutName || "Unnamed Layout",
          coordinates: [],
        };
      }
    });

    const trackData = {
      id: id,
      name: trackDetails.name,
      location: trackDetails.address,
      description: trackDetails.image,
      coordinates,
    };

    console.log("Track Data to be sent:", trackData);

    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.patch("/tracks/updateTrack", trackData, {
        headers: {
          Authorization: token,
        },
      });

      console.log(response.data);
      toast.success("Track successfully updated");
      handleMoveToTracks();
    } catch (error) {
      console.error("Failed to update track", error);
      toast.error("Failed to update track. Please try again.");
    }
  };


  // Add new layout
  const handleAddLayout = () => {
    setLayouts([...layouts, { layoutName: "", jsonInput: "" }]);
  };

  const handleDeleteLayout = (index) => {
    setLayouts(layouts.filter((_, i) => i !== index));
  };

  const handleLayoutChange = (index, field, value) => {
    const updatedLayouts = [...layouts];
    updatedLayouts[index][field] = value;
    setLayouts(updatedLayouts);
  };
  const handleMoveToTracks = () => {
    history.push("/track"); // Redirect to /add-track
  };

  return (
    <>
      <ContentWrapper>
        <Container fluid className="pb-0" id="trckfrm">
          {/* Form Fields Section with Two Columns */}
          <Row className="">
            <Col xs={12} md={5} className="form-section">
              <div className="form-group">
                <label htmlFor="trackName">Track Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="trackName"
                  placeholder="Enter track name"
                  value={trackDetails.name}
                  onChange={(e) => setTrackDetails({ ...trackDetails, name: e.target.value })}
                />
              </div>
            </Col>


            <Col xs={12} md={5}>
              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                  type="text"
                  className="form-control"
                  id="address"
                  placeholder="Enter track address"
                  value={trackDetails.address}
                  onChange={(e) => setTrackDetails({ ...trackDetails, address: e.target.value })}
                />
              </div>
            </Col>

          </Row>


          <Row>


            <Col xs={12} md={5}>
              <div className="form-group">
                <label htmlFor="image">Image</label>

                {loading ? (
                  <Spinner animation="border" role="status" />
                ) : trackDetails.image ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      style={{ width: "100%", height: "200px" }}
                      src={trackDetails.image}
                      alt="Uploaded preview"
                      onLoad={() => setImageLoaded(true)} 
                    />
                    {
                      imageLoaded && ( 
                        <button
                          onClick={handleRemoveImage}
                          style={{
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'red',
                          }}
                          aria-label="Remove Image"
                        >
                          <i className="fa-solid fa-2x fa-xmark"></i>
                        </button>
                      )
                    }
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                  <input
                    type="file"
                    className="form-control"
                    id="imageUpload"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const maxSizeInKB = 500;
                        const fileSizeInKB = file.size / 1024;
                
                        if (fileSizeInKB > maxSizeInKB) {
                          toast.error(`File size exceeds the maximum limit of ${maxSizeInKB}KB.`);
                          e.target.value = null;
                          return; 
                        }
                
                        handleImageUpload(file); 
                      }
                    }}
                    disabled={loading} 
                  />
                </div>
                
                )}
              </div>
            </Col>

          </Row>
          {layouts.map((layout, index) => (
            <React.Fragment key={index}>
              <Row className="mt-2">
                <Col xs={12} md={5} className="form-section">
                  <div className="form-group">
                    <label htmlFor={`layoutName-${index}`}>Layout Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id={`layoutName-${index}`}
                      placeholder="Enter layout name"
                      value={layout.layoutName}
                      onChange={(e) => handleLayoutChange(index, "layoutName", e.target.value)}
                    />
                  </div>
                </Col>
                <Col xs={12} md={12}>
                  <label htmlFor={`coordinates-${index}`}>Coordinates</label>
                  <textarea
                    value={layout.jsonInput}
                    className="form-control"
                    onChange={(e) => handleLayoutChange(index, "jsonInput", e.target.value)}
                    placeholder="Enter JSON coordinates"
                    style={{ width: "100%", height: "75px", marginBottom: "10px" }}
                  ></textarea>
                  <div className="d-flex align-items-center">
                    {/* <button onClick={() => handleJsonSubmit(index)} className="btn btn-info">
                      Add Layout
                    </button> */}
                    <button onClick={() => handleDeleteLayout(index)} className="btn btn-danger ml-2">
                      Delete Layout
                    </button>
                  </div>
                </Col>

              </Row>
              <Row>
                <Col xs={12} className="mt-2 mb-2">
                  <label>Map for Layout {index + 1}</label>
                  <div id={`map-${index}`} style={{ height: "400px", width: "100%", position: "relative" }}></div>
                </Col>
              </Row>
            </React.Fragment>

          ))}

          <button onClick={handleAddLayout} className="btn btn-secondary mt-2">
            Add New Layout
          </button>
          {/* Save Changes Button */}
          <Row className="mt-3" id="sc_btn">
            <Col xs={12} className="text-right">
              <button onClick={handleSaveChanges} className="btn btn-primary sv_btn">
                Save Changes
              </button>
            </Col>
          </Row>
        </Container>

        <ThinFooter />
      </ContentWrapper>
      <Toaster />

    </>
  );
};

export default UpdateTrack;
